import type { SessionStatus } from '@voxhq/web-sdk';

/** The `{ initializeVox, uploadFile }` value surface of the real core SDK. */
export type VoxSdkModule = typeof import('@voxhq/web-sdk');

/** A zero-arg async producer of the SDK module — the shape both the real lazy
 *  loader and a Cypress-injected fake conform to. */
export type VoxSdkFactory = () => Promise<VoxSdkModule>;

/**
 * Maximum `context` length VoxDenta will honour on a session.
 *
 * VoxDenta silently truncates context beyond this — VoxDenta correspondence,
 * Sep 2026.
 */
export const VOX_CONTEXT_MAX_LENGTH = 8192;

/**
 * VoxDenta's terminal session status — the wire value the vendor's `status`
 * stream emits once a session has finished writing all its notes. Typed against
 * the vendor's own `SessionStatus` union so comparison sites need no cast; the
 * single named reference to the vendor's terminal literal.
 */
export const VOX_SESSION_COMPLETED: SessionStatus = 'completed';

/**
 * Clamp free-text session context to the length VoxDenta accepts, so a caller
 * sees the same text that will reach the notes rather than a silent loss.
 *
 * VoxDenta silently truncates context beyond this — VoxDenta correspondence,
 * Sep 2026.
 */
export function truncateVoxContext(context: string): string {
  return context.slice(0, VOX_CONTEXT_MAX_LENGTH);
}

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window {
    /**
     * Test-only seam: an e2e run injects a scripted fake here, which
     * `VoxSdk.getFactory()` returns instead of the real lazy loader — but only
     * in a non-production build.
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention
    __PT_VOX_SDK_FACTORY__?: VoxSdkFactory;
    /**
     * Set by the host app at bootstrap in production builds to positively
     * disable the override seam, since a headless lib cannot read the Angular
     * `environment.production` flag directly.
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention
    __PT_VOX_SDK_PRODUCTION__?: boolean;
  }
}

/**
 * The single access point for the VoxDenta core SDK, so the rest of the app
 * never imports `@voxhq/web-sdk` directly. It keeps the browser-only SDK out of
 * the main bundle (lazy dynamic import) and owns the deterministic e2e seam.
 */
export class VoxSdk {
  private static _cachedModule: VoxSdkModule | undefined;

  /**
   * Lazily import the SDK exactly once and cache it, so a second call returns
   * the same module without re-importing — the SDK is only pulled into the
   * bundle when a scribe session actually needs it.
   */
  static async load(): Promise<VoxSdkModule> {
    if (!VoxSdk._cachedModule) {
      VoxSdk._cachedModule = await VoxSdk.importModule();
    }
    return VoxSdk._cachedModule;
  }

  /**
   * Resolve the factory to use: a Cypress-injected fake when one is present in
   * a non-production build, otherwise the real lazy loader. This is the seam
   * that lets e2e drive scripted `status` / `notes` events deterministically.
   */
  static getFactory(): VoxSdkFactory {
    return VoxSdk._testOverride() ?? VoxSdk.load;
  }

  /** Drop the cached module so the next `load()` re-imports — for tests. */
  static reset(): void {
    VoxSdk._cachedModule = undefined;
  }

  /**
   * The uncached dynamic import, kept public so `jest.spyOn(VoxSdk,
   * 'importModule')` can intercept it (a bare dynamic import could not be
   * spied) and a spec can assert `load()` imports only once. Callers use
   * `load()` for the cached module.
   */
  static importModule(): Promise<VoxSdkModule> {
    return import('@voxhq/web-sdk');
  }

  /**
   * The injected factory, but only when the override seam is open. The seam is
   * closed in two cases: a production build, where the host app sets
   * `window.__PT_VOX_SDK_PRODUCTION__` at bootstrap to positively disable it (a
   * headless lib cannot read Angular's `environment.production`); and any
   * context without a `window` — Node / SSR / unit tests — where no fake can be
   * injected in the first place.
   */
  private static _testOverride(): VoxSdkFactory | undefined {
    if (
      typeof window === 'undefined' ||
      window.__PT_VOX_SDK_PRODUCTION__ === true
    ) {
      return;
    }
    return window.__PT_VOX_SDK_FACTORY__;
  }
}
